import { impliedCirculatingSupply } from "@/lib/cypherpunk/networkShare";
import { formatDateLabel, formatNumberFixed, formatUsdCompact, formatZec } from "@/lib/cypherpunk/format";
import { CYPH_WAFFLE_HERO } from "@/constants/cypherpunkTracker";
import type { CypherpunkTrackerPayload } from "@/types/cypherpunkTrackerData";
import NetworkOwnershipWaffle from "./NetworkOwnershipWaffle";

export default function NetworkSharePosition({
  data,
}: {
  data: CypherpunkTrackerPayload;
}): React.ReactElement {
  const ownershipPct = formatNumberFixed(data.goal.currentNetworkPct);
  const firstPurchaseDate = formatDateLabel(data.summary.firstPurchaseDate);
  const circulatingSupply = impliedCirculatingSupply(
    data.summary.totalZecHeld,
    data.goal.currentNetworkPct,
  );

  return (
    <div className="card cyph-defer-paint cyph-hero-card">
      <div className="card-head">
        <div>
          <div className="card-title">{CYPH_WAFFLE_HERO.title}</div>
          <div className="card-sub">{CYPH_WAFFLE_HERO.subtitle}</div>
        </div>
      </div>

      <div className="cyph-hero-body">
        <div className="cyph-hero-waffle">
          <NetworkOwnershipWaffle
            networkOwnershipPct={data.goal.currentNetworkPct}
            targetPct={data.goal.targetPct}
            circulatingSupply={circulatingSupply}
          />
        </div>

        <div className="cyph-hero-aside">
          <div className="cyph-hero-headline">
            <div className="cyph-hero-eyebrow">Network ownership</div>
            <div className="cyph-hero-pct">
              <span className="cyph-num">{ownershipPct}</span>
              <span className="cyph-hero-pct-suffix">%</span>
            </div>
            <p className="cyph-hero-caption">
              of all circulating Zcash held by{" "}
              <span className="cyph-hero-caption-accent">Cypherpunk Technologies</span>
            </p>
          </div>

          <div className="cyph-hero-stats">
            <div className="cyph-hero-stat">
              <div className="cyph-hero-stat-label">Cypherpunk target</div>
              <div className="cyph-hero-stat-value">
                <span className="cyph-num">{formatNumberFixed(data.goal.targetPct)}</span>%
              </div>
              <div className="cyph-hero-stat-delta">Network ownership</div>
            </div>

            <div className="cyph-hero-stat">
              <div className="cyph-hero-stat-label">Days since</div>
              <div className="cyph-hero-stat-value">
                <span className="cyph-num">{data.summary.daysSinceFirstBuy.toLocaleString("en-US")}</span>
              </div>
              <div className="cyph-hero-stat-delta">since {firstPurchaseDate}</div>
            </div>

            <div className="cyph-hero-stat">
              <div className="cyph-hero-stat-label">Total ZEC</div>
              <div className="cyph-hero-stat-value">
                <span className="cyph-num">{formatZec(data.summary.totalZecHeld)}</span>
              </div>
              <div className="cyph-hero-stat-delta">
                ~<span className="cyph-num">{formatUsdCompact(data.display.reserveValueUsd)}</span> at current
                price
              </div>
            </div>

            <div className="cyph-hero-stat">
              <div className="cyph-hero-stat-label">Tot deploy</div>
              <div className="cyph-hero-stat-value">
                <span className="cyph-num">{formatUsdCompact(data.summary.totalCostUsd)}</span>
              </div>
              <div className="cyph-hero-stat-delta">
                Avg cost:{" "}
                <span className="cyph-hero-stat-accent">
                  {data.summary.avgCostBasisUsd != null ? (
                    <span className="cyph-num">{formatUsdCompact(data.summary.avgCostBasisUsd)}</span>
                  ) : (
                    "—"
                  )}
                </span>{" "}
                per ZEC
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
